import React from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import type { TriggerProps } from '@atlaskit/popup/types';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { i18n } from './messages.ts';
import { InfoContainer, FeedbackContainer } from './SharedComponents.tsx';

type EndOfPageRecommendationInfoPopupProps = {
	toggleInfo: () => void;
	showFeedbackButton?: boolean;
	clickFeedback?: () => void;
	isInfoOpen: boolean;
	handleInfoButtonRef: (triggerProps: TriggerProps) => (node: HTMLButtonElement | null) => void;
};

export const EndOfPageRecommendationInfoPopup = ({
	toggleInfo,
	showFeedbackButton,
	clickFeedback,
	isInfoOpen,
	handleInfoButtonRef,
}: EndOfPageRecommendationInfoPopupProps): React.JSX.Element => {
	const { Popup } = useChoreographerAssets();

	return (
		<Popup
			shouldRenderToParent
			strategy="absolute"
			onClose={toggleInfo}
			content={() => (
				<InfoContainer>
					<FormattedMessage {...i18n.info} />
					{showFeedbackButton && (
						<FeedbackContainer>
							<Button onClick={clickFeedback}>
								<FormattedMessage {...i18n.feedback} />
							</Button>
						</FeedbackContainer>
					)}
				</InfoContainer>
			)}
			isOpen={isInfoOpen}
			placement="top-start"
			trigger={(triggerProps) => (
				<IconButton
					appearance="subtle"
					onClick={toggleInfo}
					isSelected={isInfoOpen}
					testId="eop-info-button"
					spacing="compact"
					icon={(iconProps) => <InformationCircleIcon {...iconProps} size="small" />}
					label={<FormattedMessage {...i18n.infoButton} />}
					{...triggerProps}
					ref={handleInfoButtonRef(triggerProps)}
				/>
			)}
		/>
	);
};
